/**
 * WellnessReportsPage — V2 main orchestrator for Wellness Reports.
 *
 * Supports 5 report types (check-in, missed-check-in, check-in-history,
 * rounds, rounds-history) with Summary/Detailed view toggle, filter drawer,
 * sort, column filters, pagination, and CSV/PDF export.
 *
 * Replaces V1 WellnessReports.jsx (~2100 lines) with clean V2 architecture.
 */

import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import NavigationV2 from '../../../components/v2/NavigationV2.jsx';
import Pagination from '../../../components/v2/Pagination.jsx';
import Toast from '../../../components/v2/Toast.jsx';
import useToast from '../../../hooks/useToast.js';
import {
    getSession,
    getCampusId,
    getCampusList,
    updateSessionCampus,
    getUsername,
    getUserId,
    getSessionId,
    getCampusConfig,
    getCampusTzOffset,
} from '../../../services/session.js';
import { iAmHigherAuthority, formatDateIso, formatDateTimeIso, formatDateTime12Hour, getToday, campusNow } from '../../../libs.jsx';
import { exportWellnessCsv, exportWellnessPdf } from '../../../services/csvExport.js';
import { fetchWellnessReport, fetchWellnessFilterOptions } from './api/wellnessApi.js';

import WellnessTable, { COLUMN_DEFS_MAP } from './components/WellnessTable.jsx';
import ReportFilterDrawer from '../alert-details/components/ReportFilterDrawer.jsx';
import { WELLNESS_REPORT_SECTIONS } from '../../../components/v2/reportFilterSections.js';
import ScheduleReportDrawer from '../alert-details/components/ScheduleReportDrawer.jsx';
import { fetchFrequencies, createSchedule } from '../scheduled/api/scheduledApi.js';

import usePersistedPerPage from '../../../hooks/usePersistedPerPage.js';
import './wellness-reports-page.css';
import { sortRows } from '../../../utils/sortRows.js';
import { reportTypeIdForSubType, getReportType } from '../../../utils/reportTypes.js';
import { buildFilterChips, countActiveFilters } from '../../../utils/reportFilterChips.js';
import { TIME_RANGES, initialRangeState, rangeStateFor, reportErrorMessage, clearCampusFilters } from '../../../utils/reportPageState.js';
import useReportRestore from '../../../hooks/useReportRestore.js';
import { ReportRestoreOffer, ReportRestoreNote } from '../../../components/v2/ReportRestoreBanner.jsx';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------


/**
 * Report types, tagged with the scope that owns them. What was one "Wellness"
 * page with all five in a single dropdown is now two pages — Check-in Reports and
 * Rounds Reports — each showing only its own group. The page body is otherwise
 * identical for both, so scope filters this list rather than forking the page.
 */
const REPORT_TYPES = [
    { value: 'check-in', label: 'Check-in Analysis', scope: 'checkin' },
    { value: 'missed-check-in', label: 'Missed Check-in', scope: 'checkin' },
    { value: 'check-in-history', label: 'Check-in History', scope: 'checkin' },
    { value: 'rounds', label: 'Rounds Analysis', scope: 'rounds' },
    { value: 'rounds-history', label: 'Rounds History', scope: 'rounds' },
];

/* Restore-cache scopes (useReportRestore): literals, never the route param. One per page
   scope, so the Check-in page's last run is never offered on the Rounds page. The report
   type within a scope is part of the config (`subType`), so Back restores the report that
   was actually on screen. */
const REPORT_TYPE_BY_SCOPE = { checkin: 'wellness-checkin', rounds: 'wellness-rounds' };

const SCOPES = {
    checkin: { title: 'Check-in', defaultType: 'check-in' },
    rounds: { title: 'Rounds', defaultType: 'rounds' },
};

/** Which scope owns a report type — used to route legacy /reports/wellness links. */
export function scopeForReportType(value) {
    return REPORT_TYPES.find((rt) => rt.value === value)?.scope || null;
}

/** Falls back to check-in for an unknown scope so a bad route still renders. */
export function scopeMeta(scope) {
    return SCOPES[scope] || SCOPES.checkin;
}

/** The report types this scope's dropdown offers. */
export function reportTypesForScope(scope) {
    const resolved = SCOPES[scope] ? scope : 'checkin';
    return REPORT_TYPES.filter((rt) => rt.scope === resolved);
}

/**
 * The report type a page should open on: an incoming subType (from Scheduled
 * "Run Report Local") only when it belongs to this scope, else the scope default.
 */
export function initialReportType(scope, requestedSubType) {
    const resolved = SCOPES[scope] ? scope : 'checkin';
    return scopeForReportType(requestedSubType) === resolved
        ? requestedSubType
        : SCOPES[resolved].defaultType;
}


/**
 * The filter groups a report type's drawer, chips and badge use: its schedule preset
 * (utils/reportTypes.js), which adds a windows group to Check-in, Missed Check-in and Rounds
 * (docs/CHECKIN_WINDOW_FILTER_PLAN.md). The History reports keep the plain wellness groups.
 */
export function sectionsForReportType(reportType) {
    return getReportType(reportTypeIdForSubType('wellness', reportType, null))?.filters || WELLNESS_REPORT_SECTIONS;
}

/** The report type has a windows filter. */
function hasWindowsFilter(reportType) {
    return sectionsForReportType(reportType).some((sec) => sec.subs.some((sub) => sub.key === 'windows'));
}

function emptyReportFilters() {
    return {
        windows: [],
        areas: [],
        areaGroups: [],
        areaTypes: [],
        residents: [],
        residentGroups: [],
        deviceCategories: [],
    };
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const WellnessReportsPage = ({ scope = 'checkin' }) => {
    const scopeDef = scopeMeta(scope);
    // Only this scope's report types appear in the dropdown.
    const reportTypeOptions = useMemo(() => reportTypesForScope(scope), [scope]);

    // -- Session --
    const session = useMemo(() => getSession(), []);
    const campusList = useMemo(() => getCampusList() || [], []);
    const [campusId, setCampusId] = useState(() => getCampusId());
    const campusConfig = useMemo(() => getCampusConfig(campusId), [campusId]);
    const tzOffset = getCampusTzOffset(campusId);

    // -- Report restoration (history stamp + result cache + nav-menu offer) --
    /* Supplies the navigation state this page used to read directly (ScheduledReportsPage
       "Run Report Local", and a stamped history entry on browser Back). Config keys are the
       ones the initialisers below read: subType, rangeId, filters, startDate, endDate. */
    const restore = useReportRestore({ reportType: REPORT_TYPE_BY_SCOPE[scope] || REPORT_TYPE_BY_SCOPE.checkin, campusId });
    const { lookup: cacheLookup, commit: cacheCommit, endRefresh } = restore;
    const navState = restore.navState;

    // -- Report type --
    // A subType arriving from ScheduledReportsPage's "Run Report Local" is only
    // honoured when it belongs to this scope; otherwise the scope default wins, so
    // a Rounds schedule can't strand a Rounds type inside the Check-in dropdown.
    const [reportType, setReportType] = useState(() => initialReportType(scope, navState?.subType));
    const isMissedCheckin = reportType === 'missed-check-in';
    const filterSections = useMemo(() => sectionsForReportType(reportType), [reportType]);

    // -- Report state --
    const [status, setStatus] = useState('init');
    const [reportData, setReportData] = useState([]);
    const [summaryData, setSummaryData] = useState([]);
    const [overLimit, setOverLimit] = useState(false);
    const [csvUrl, setCsvUrl] = useState('');

    // -- Time range --
    const initRange = initialRangeState(navState);
    const [rangeId, setRangeId] = useState(initRange.rangeId);
    const [rangeName, setRangeName] = useState(initRange.rangeName);
    const [customRange, setCustomRange] = useState(initRange.customRange);
    const [start, setStart] = useState(() => {
        if (navState?.startDate) return new Date(navState.startDate);
        const d = getToday(-1); d.setHours(0,0,0,0); return d;
    });
    const [end, setEnd] = useState(() => {
        if (navState?.endDate) return new Date(navState.endDate);
        const d = getToday(-1); d.setHours(23,59,59,999); return d;
    });
    /* The range in use before Missed Check-in forced Today (see handleReportTypeChange). */
    const savedRangeRef = useRef(null);
    const [startDisplay, setStartDisplay] = useState('');
    const [endDisplay, setEndDisplay] = useState('');

    // -- Report filters — only accept filter keys that wellness understands.
    //    parseFilterTerms() in scheduleRunHelpers returns extra keys (users,
    //    devices, responseTime, ...) that other report pages use; copying
    //    non-array shapes into reportFilters breaks the filter drawer's
    //    assumption that every value is an array. --
    const [reportFilters, setReportFilters] = useState(() => {
        const base = emptyReportFilters();
        if (!navState?.filters) return base;
        const allowed = {};
        Object.keys(base).forEach((k) => {
            if (Array.isArray(navState.filters[k])) allowed[k] = navState.filters[k];
        });
        return { ...base, ...allowed };
    });
    const [filterOptions, setFilterOptions] = useState({});
    const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);

    // -- View mode --
    const [viewMode, setViewMode] = useState(() => (
        initialReportType(scope, navState?.subType) === 'missed-check-in' ? 'detailed' : 'summary'));

    // -- Table state --
    const [search, setSearch] = useState('');
    const [columnFilters, setColumnFilters] = useState({});
    const [sortColumn, setSortColumn] = useState('');
    const [sortOrder, setSortOrder] = useState('asc');
    const [page, setPage] = useState(1);
    const [perPage, setPerPage] = usePersistedPerPage();

    // -- Active column defs --
    const activeColumnDefs = useMemo(() => {
        return COLUMN_DEFS_MAP[reportType]?.[viewMode] || [];
    }, [reportType, viewMode]);

    const [visibleColumns, setVisibleColumns] = useState(() =>
        (COLUMN_DEFS_MAP['check-in']?.summary || []).map((c) => c.key)
    );

    // -- Schedule drawer --
    const [scheduleDrawerOpen, setScheduleDrawerOpen] = useState(false);
    const [frequencies, setFrequencies] = useState([]);

    // -- Time popover --
    const [timePopoverOpen, setTimePopoverOpen] = useState(false);
    const timePopoverRef = useRef(null);
    const pendingAutoRunRef = useRef(false);

    // -- UI --
    const shouldRerunRef = useRef(false);

    const { toast, showToast: showTypedToast, dismissToast } = useToast();
    /* This page's calls are (message, type); the shared hook is (type, message). */
    const showToast = useCallback((message, type = 'success') => showTypedToast(type, message), [showTypedToast]);

    // ======================================================================
    // Update visible columns when report type or view mode changes
    // ======================================================================
    useEffect(() => {
        const cols = COLUMN_DEFS_MAP[reportType]?.[viewMode] || [];
        setVisibleColumns(cols.map((c) => c.key));
        setColumnFilters({});
        setSearch('');
        setPage(1);
        if (cols.length > 0) {
            setSortColumn(cols[0].key);
            setSortOrder('asc');
        }
    }, [reportType, viewMode]);

    // ======================================================================
    // Load filter options on mount / campus change
    // ======================================================================
    useEffect(() => {
        if (!campusId) return;
        fetchWellnessFilterOptions(campusId).then(setFilterOptions);
        fetchFrequencies().then((d) => setFrequencies(Array.isArray(d) ? d : []));
    }, [campusId]);

    // ======================================================================
    // Close time popover on outside click / Escape
    // ======================================================================
    useEffect(() => {
        if (!timePopoverOpen) return;
        const handleClick = (e) => {
            if (timePopoverRef.current && !timePopoverRef.current.contains(e.target)) {
                setTimePopoverOpen(false);
            }
        };
        const handleKey = (e) => {
            if (e.key === 'Escape') setTimePopoverOpen(false);
        };
        document.addEventListener('mousedown', handleClick);
        document.addEventListener('keydown', handleKey);
        return () => {
            document.removeEventListener('mousedown', handleClick);
            document.removeEventListener('keydown', handleKey);
        };
    }, [timePopoverOpen]);

    // ======================================================================
    // Time range helpers
    // ======================================================================
    const handleTimeRange = useCallback((range) => {
        const [s, e] = range.getRange();
        setStart(s);
        setEnd(e);
        setRangeId(range.id);
        setRangeName(range.label);
        setCustomRange(false);
    }, []);

    const handleCustomToggle = useCallback(() => {
        setCustomRange(true);
        setRangeId(0);
        setRangeName('Custom');
    }, []);

    const handlePopoverCustom = useCallback(() => {
        setTimePopoverOpen(false);
        handleCustomToggle();
        setFilterDrawerOpen(true);
    }, [handleCustomToggle]);

    // ======================================================================
    // Report type change
    // ======================================================================
    const handleReportTypeChange = useCallback((e) => {
        const newType = e.target.value;
        setReportType(newType);
        /* Check-in and rounds windows don't mix, and the History reports have no windows
           filter: keep the selection only when the new type lists the same windows. */
        if (sectionsForReportType(newType) !== sectionsForReportType(reportType)) {
            setReportFilters((prev) => ({ ...prev, windows: [] }));
        }

        if (newType === 'missed-check-in') {
            setViewMode('detailed');
            /* Missed Check-in always runs for Today. Remember the range it replaces, so
               switching back to another report restores it; otherwise that report silently
               reran for Today and read like the Missed results still on screen. */
            if (reportType !== 'missed-check-in') {
                savedRangeRef.current = { start, end, rangeId, rangeName, customRange };
            }
            // Force Today
            const todayRange = TIME_RANGES.find((r) => r.id === 1);
            if (todayRange) {
                const [s, en] = todayRange.getRange();
                setStart(s);
                setEnd(en);
                setRangeId(1);
                setRangeName('Today');
                setCustomRange(false);
            }
        } else {
            setViewMode('summary');
            const saved = savedRangeRef.current;
            if (reportType === 'missed-check-in' && saved) {
                savedRangeRef.current = null;
                setStart(saved.start);
                setEnd(saved.end);
                setRangeId(saved.rangeId);
                setRangeName(saved.rangeName);
                setCustomRange(saved.customRange);
            }
        }

        // Reset table state
        setSearch('');
        setColumnFilters({});
        setPage(1);

        // Re-run if report was already loaded
        if (status !== 'init') {
            shouldRerunRef.current = true;
        }
    }, [status, reportType, start, end, rangeId, rangeName, customRange]);

    // ======================================================================
    // View mode change
    // ======================================================================
    const handleViewModeChange = useCallback((mode) => {
        setViewMode(mode);
        setSearch('');
        setColumnFilters({});
        setPage(1);
    }, []);

    // ======================================================================
    // Run Report
    // ======================================================================
    /* `force` skips the cache. Every Run control passes it: a cache that ignored an
       explicit Run would be indistinguishable from a broken button. */
    const handleRunReport = useCallback(async ({ force = false } = {}) => {
        // Cap end to now, in campus time like `end` itself (campusNow, libs.jsx): a browser
        // clock behind the campus would clip the campus's latest alerts.
        const now = campusNow();
        const endToUse = end > now ? now : end;

        // Wellness API expects date-only format (YYYY-MM-DD), not datetime
        const stD = formatDateIso(start);
        const enD = formatDateIso(endToUse);

        setStartDisplay(formatDateTime12Hour(start));
        setEndDisplay(formatDateTime12Hour(endToUse));

        /* Key names match what the state initialisers read off navState -- the hook's one
           contract. subType is the report type, which a restored entry must bring back. */
        const runConfig = {
            subType: reportType,
            rangeId,
            filters: reportFilters,
            startDate: start.toISOString(),
            endDate: endToUse.toISOString(),
        };
        const { hit, revalidating } = cacheLookup(runConfig, { force });
        /* A check-in window still in progress when the cached run was made has moved on
           since: show the snapshot at once, but always refresh it underneath, so "In
           progress" never stays on screen describing a moment that has passed. */
        const refreshInProgress = Boolean(hit && (hit.records || []).some((r) => r.in_progress));
        if (hit) {
            setReportData(hit.records || []);
            setSummaryData(hit.summary || []);
            setOverLimit(Boolean(hit.overLimit));
            setCsvUrl(hit.csvUrl || '');
            setPage(1);
            setStatus('ready');
            if (!revalidating && !refreshInProgress) return;
        } else {
            setStatus('loading');
            setOverLimit(false);
        }

        const filterPayload = {
            areas: (reportFilters.areas || []).join(','),
            areaGroups: (reportFilters.areaGroups || []).join(','),
            areaTypes: (reportFilters.areaTypes || []).join(','),
            residents: (reportFilters.residents || []).join(','),
            residentGroups: (reportFilters.residentGroups || []).join(','),
            deviceCategories: (reportFilters.deviceCategories || []).join(','),
            ...(hasWindowsFilter(reportType) ? { windows: (reportFilters.windows || []).join(',') } : {}),
        };

        const result = await fetchWellnessReport({
            campusId,
            username: getUsername(),
            startDate: stD,
            endDate: enD,
            reportType,
            filters: filterPayload,
            tzOffset: getCampusTzOffset(campusId),
        });

        if (result.ErrorCode) {
            showToast(reportErrorMessage(result.ErrorCode), 'error');
            /* A failed BACKGROUND refresh leaves the restored report up rather than
               blanking a report someone is reading. */
            if (revalidating || refreshInProgress) {
                endRefresh();
                return;
            }
            setStatus('init');
            return;
        }

        if (result.overlimit) {
            setOverLimit(true);
            setCsvUrl(result.url || '');
            showToast('Report exceeds 5,000 events. Details will need to be downloaded.', 'warning');
        } else {
            setOverLimit(false);
            setCsvUrl(result.url || '');
        }

        setReportData(result.records || []);
        setSummaryData(result.summary || []);
        setPage(1);
        setStatus('ready');

        cacheCommit(runConfig, {
            records: result.records || [],
            summary: result.summary || [],
            overLimit: Boolean(result.overlimit),
            csvUrl: result.url || '',
        });
    }, [start, end, campusId, reportType, reportFilters, showToast, rangeId,
        cacheLookup, cacheCommit, endRefresh]);

    // ======================================================================
    // Re-run after filter apply / type change (if already run)
    // ======================================================================
    useEffect(() => {
        if (shouldRerunRef.current) {
            shouldRerunRef.current = false;
            handleRunReport();
        }
    }, [reportFilters, reportType, handleRunReport]);

    // ======================================================================
    // Auto-run on mount: Run Report Local, or a stamped history entry on browser
    // Back. consumeAutoRun is the hook's one-shot, so the two cannot double fire.
    // ======================================================================
    /* Consumed in an effect, not during render: the hook's own (earlier) effect reads the
       un-consumed one-shot to tell a Back return from a fresh arrival before offering the
       last run. Effects fire in declaration order, so the firing effect below sees it. */
    const autoRunArmedRef = useRef(false);
    useEffect(() => {
        autoRunArmedRef.current = restore.consumeAutoRun();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps
    useEffect(() => {
        if (!autoRunArmedRef.current) return;
        autoRunArmedRef.current = false;
        handleRunReport();
    }, [handleRunReport]);

    /* Accepting the nav-menu offer. Deferred through pendingAutoRunRef rather than run
       here: setState is async, so a run started now would key the cache off the OLD
       config and miss. */
    const handleAcceptRestore = useCallback(() => {
        const cfg = restore.acceptOffer();
        if (!cfg) return;
        const type = initialReportType(scope, cfg.subType);
        setReportType(type);
        setViewMode(type === 'missed-check-in' ? 'detailed' : 'summary');
        const r = rangeStateFor(cfg.rangeId ?? 0);
        setRangeId(r.rangeId);
        setRangeName(r.rangeName);
        setCustomRange(r.customRange);
        if (cfg.startDate) setStart(new Date(cfg.startDate));
        if (cfg.endDate) setEnd(new Date(cfg.endDate));
        if (cfg.filters) setReportFilters({ ...emptyReportFilters(), ...cfg.filters });
        pendingAutoRunRef.current = true;
    }, [restore, scope]);

    // ======================================================================
    // Auto-run after quick time-range selection from popover
    // ======================================================================
    useEffect(() => {
        if (pendingAutoRunRef.current) {
            pendingAutoRunRef.current = false;
            handleRunReport();
        }
    });

    const handleQuickTimeRange = useCallback((range) => {
        handleTimeRange(range);
        pendingAutoRunRef.current = true;
        setTimePopoverOpen(false);
    }, [handleTimeRange]);

    // ======================================================================
    // Campus change
    // ======================================================================
    const handleCampusChange = useCallback((e) => {
        const val = parseInt(e.target.value, 10);
        setCampusId(val);
        updateSessionCampus(val);
        /* The filter lists hold the old campus's ids, which would still be sent (A21). */
        setReportFilters(clearCampusFilters);
        setStatus('init');
        setReportData([]);
        setSummaryData([]);
    }, []);

    // ======================================================================
    // Data pipeline: select dataset → search → column filters → sort → paginate
    // ======================================================================
    const activeData = viewMode === 'summary' ? summaryData : reportData;
    /* A reported window hasn't ended yet (the Lambda marks its rows in_progress). */
    const hasInProgress = useMemo(() => reportData.some((r) => r.in_progress), [reportData]);

    const searchedData = useMemo(() => {
        if (!search) return activeData;
        const term = search.toLowerCase();
        return activeData.filter((row) =>
            (row.resident_name || '').toLowerCase().includes(term) ||
            (row.area_name || '').toLowerCase().includes(term) ||
            (row.device_name || '').toLowerCase().includes(term) ||
            (row.manufacture_uuid || '').toLowerCase().includes(term)
        );
    }, [activeData, search]);

    const columnFilteredData = useMemo(() => {
        const active = Object.entries(columnFilters).filter(([, vals]) => vals.length > 0);
        if (active.length === 0) return searchedData;
        return searchedData.filter((row) =>
            active.every(([key, vals]) => {
                const val = row[key] != null ? String(row[key]) : '';
                return vals.includes(val);
            })
        );
    }, [searchedData, columnFilters]);

    // Blank values sort last in both directions (utils/sortRows.js).
    const sortedData = useMemo(
        () => sortRows(columnFilteredData, sortColumn, sortOrder, {
            numericKeys: ['windows_count', 'check_in_count', 'missed_checkin_count', 'rounds_count', 'missed_rounds_count', 'count'],
        }),
        [columnFilteredData, sortColumn, sortOrder],
    );

    const totalFiltered = sortedData.length;
    const totalPages = Math.max(1, Math.ceil(totalFiltered / perPage));
    const safePage = Math.min(page, totalPages);
    const paginatedData = useMemo(() => {
        const startIdx = (safePage - 1) * perPage;
        return sortedData.slice(startIdx, startIdx + perPage);
    }, [sortedData, safePage, perPage]);

    // ======================================================================
    // Handlers
    // ======================================================================
    const handleSort = useCallback((colKey) => {
        setSortColumn((prev) => {
            if (prev === colKey) {
                setSortOrder((o) => (o === 'asc' ? 'desc' : 'asc'));
                return prev;
            }
            setSortOrder('asc');
            return colKey;
        });
        setPage(1);
    }, []);

    const handleColumnFilterChange = useCallback((columnKey, selectedValues) => {
        setColumnFilters((prev) => ({ ...prev, [columnKey]: selectedValues }));
        setPage(1);
    }, []);

    const handleClearColumnFilters = useCallback(() => {
        setColumnFilters({});
        setPage(1);
    }, []);

    const handleApplyFilters = useCallback((filters) => {
        setReportFilters(filters);
        setFilterDrawerOpen(false);
        if (status === 'ready') {
            shouldRerunRef.current = true;
        }
    }, [status]);

    const handleClearReportFilters = useCallback(() => {
        setReportFilters(emptyReportFilters());
        if (status === 'ready') {
            shouldRerunRef.current = true;
        }
    }, [status]);

    // Filter chip generation
    /* utils/reportFilterChips.js — names resolved per sub-section idKey (audit B16). */
    const reportFilterChips = useMemo(() => buildFilterChips(reportFilters, filterOptions, filterSections), [reportFilters, filterOptions, filterSections]);

    /* Active filters, not selected items — the same badge on every report (audit A22). */
    const activeReportFilterCount = useMemo(() => countActiveFilters(reportFilters, filterSections), [reportFilters, filterSections]);

    /* What a new schedule stores: no window_filter key for a report without the filter, so
       its report_filter_terms stay exactly as before (utils/reportFilterTerms.js). */
    const scheduleFilters = useMemo(() => {
        if (hasWindowsFilter(reportType)) return reportFilters;
        const { windows, ...rest } = reportFilters;
        return rest;
    }, [reportFilters, reportType]);

    // Schedule
    const handleScheduleSave = useCallback(async (payload) => {
        const result = await createSchedule(payload, { campusId, userId: getUserId(), clientId: getSessionId() });
        if (result[0]) { showToast('Schedule created successfully.'); setScheduleDrawerOpen(false); }
        else { showToast(result[1] || 'Failed to create schedule.', 'error'); }
    }, [campusId, showToast]);

    // Export
    const exportColumnDefs = useMemo(() =>
        activeColumnDefs.filter((col) => visibleColumns.includes(col.key)),
    [activeColumnDefs, visibleColumns]);

    const handleExportCsv = useCallback(() => {
        const repLabel = REPORT_TYPES.find((r) => r.value === reportType)?.label || reportType;
        const filename = `wellness-${reportType}-${viewMode}.csv`;
        exportWellnessCsv(sortedData, exportColumnDefs, filename);
    }, [sortedData, exportColumnDefs, reportType, viewMode]);

    const handleExportPdf = useCallback(() => {
        const repLabel = REPORT_TYPES.find((r) => r.value === reportType)?.label || reportType;
        const dateRange = startDisplay && endDisplay ? `${startDisplay} – ${endDisplay}` : '';
        const filename = `wellness-${reportType}-${viewMode}.pdf`;
        exportWellnessPdf(sortedData, exportColumnDefs, `Wellness: ${repLabel} (${viewMode})`, dateRange, filename);
    }, [sortedData, exportColumnDefs, reportType, viewMode, startDisplay, endDisplay]);

    // ======================================================================
    // RENDER
    // ======================================================================
    return (
        <>
            <NavigationV2 />
            <div className="nav2-main">
                <div className="ps-page">
                    {/* Toast */}
                    <Toast toast={toast} onDismiss={dismissToast} />

                    {/* PAGE HEADER */}
                    <div className="ps-page-header">
                        <div className="ps-page-header__inner">
                            <div className="ps-breadcrumb-row">
                                <div className="ps-breadcrumb">
                                    <span className="ps-breadcrumb__text">Reports</span>
                                    <svg className="ar-icon-md ps-breadcrumb__separator" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
                                    </svg>
                                    <span className="ps-breadcrumb__current">Wellness</span>
                                </div>

                                <div className="ps-campus-row">
                                    <label htmlFor="wr-campus-selector" className="ps-campus-label">Campus:</label>
                                    {campusList.length > 1 ? (
                                        <select
                                            id="wr-campus-selector"
                                            value={campusId}
                                            onChange={handleCampusChange}
                                            className="ps-campus-select"
                                        >
                                            {campusList.map((c) => (
                                                <option key={c.campus_id} value={c.campus_id}>{c.campus_name}</option>
                                            ))}
                                        </select>
                                    ) : (
                                        <span className="ps-campus-name">{campusConfig?.name || ''}</span>
                                    )}
                                </div>
                            </div>

                            <div className="ar-title-row">
                                <div className="ar-title-row__left">
                                    <h1 className="ps-page-title">{scopeDef.title}</h1>
                                    <select
                                        className="wr-report-type-select"
                                        value={reportType}
                                        onChange={handleReportTypeChange}
                                        title="Report type"
                                    >
                                        {reportTypeOptions.map((rt) => (
                                            <option key={rt.value} value={rt.value}>{rt.label}</option>
                                        ))}
                                    </select>
                                </div>
                                <div className="ar-title-row__actions">
                                    <div className="ar-time-badge-wrap" ref={timePopoverRef}>
                                        <span
                                            className={`ar-time-summary${timePopoverOpen ? ' ar-time-summary--open' : ''}`}
                                            onClick={() => setTimePopoverOpen((p) => !p)}
                                            role="button"
                                            tabIndex={0}
                                            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setTimePopoverOpen((p) => !p); } }}
                                            aria-expanded={timePopoverOpen}
                                            aria-haspopup="true"
                                        >
                                            <i className="fe fe-clock" style={{ marginRight: '4px', fontSize: 'var(--font-size-xs)' }} />
                                            {customRange
                                                ? `${formatDateTime12Hour(start)} – ${formatDateTime12Hour(end)}`
                                                : rangeName}
                                            <svg className="ar-time-summary__chevron" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                                                <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
                                            </svg>
                                        </span>

                                        {timePopoverOpen ? (
                                            <div className="ar-time-popover">
                                                <div className="ar-time-popover__grid">
                                                    {TIME_RANGES.map((range) => (
                                                        <button
                                                            key={range.id}
                                                            type="button"
                                                            className={`ar-time-popover__btn${rangeId === range.id && !customRange ? ' ar-time-popover__btn--active' : ''}`}
                                                            onClick={() => handleQuickTimeRange(range)}
                                                        >
                                                            {range.label}
                                                        </button>
                                                    ))}
                                                </div>
                                                <div className="ar-time-popover__divider" />
                                                <button
                                                    type="button"
                                                    className={`ar-time-popover__custom${customRange ? ' ar-time-popover__custom--active' : ''}`}
                                                    onClick={handlePopoverCustom}
                                                >
                                                    <svg width="14" height="14" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                                                        <path strokeLinecap="round" strokeLinejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 012.25-2.25h13.5A2.25 2.25 0 0121 7.5v11.25m-18 0A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75m-18 0v-7.5A2.25 2.25 0 015.25 9h13.5A2.25 2.25 0 0121 11.25v7.5" />
                                                    </svg>
                                                    Custom Range…
                                                </button>
                                            </div>
                                        ) : null}
                                    </div>
                                    <span className="ar-disabled-tooltip-wrap">
                                        <button
                                            className="dp-btn dp-btn--secondary dp-btn--sm"
                                            onClick={() => setScheduleDrawerOpen(true)}
                                            disabled={customRange}
                                        >
                                            <i className="fe fe-calendar" style={{ marginRight: '4px' }} />Schedule Report
                                        </button>
                                        {customRange ? (
                                            <span className="ar-disabled-tooltip" role="tooltip">
                                                Static custom dates are not eligible for recurring scheduled reporting.
                                            </span>
                                        ) : null}
                                    </span>
                                    <button
                                        className="dp-btn dp-btn--secondary dp-btn--sm"
                                        onClick={() => setFilterDrawerOpen(true)}
                                        style={{ position: 'relative' }}
                                    >
                                        <i className="fe fe-sliders" style={{ marginRight: '4px' }} />
                                        Report Settings
                                        {activeReportFilterCount > 0 ? (
                                            <span className="ar-filter-badge">{activeReportFilterCount}</span>
                                        ) : null}
                                    </button>
                                    <button
                                        className="dp-btn dp-btn--primary dp-btn--sm"
                                        onClick={() => handleRunReport({ force: true })}
                                        disabled={status === 'loading'}
                                    >
                                        <i className="fe fe-refresh-cw" style={{ marginRight: '4px' }} />
                                        {status === 'loading' ? 'Running...' : 'Run Report'}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* View toggle — hidden for missed-check-in */}
                    {status === 'ready' && !overLimit && !isMissedCheckin ? (
                        <div className="ar-subtab-bar">
                            <div className="pp-subtab-toggle">
                                <button
                                    className={`pp-subtab-btn${viewMode === 'summary' ? ' is-active' : ''}`}
                                    onClick={() => handleViewModeChange('summary')}
                                >
                                    Summary
                                </button>
                                <button
                                    className={`pp-subtab-btn${viewMode === 'detailed' ? ' is-active' : ''}`}
                                    onClick={() => handleViewModeChange('detailed')}
                                >
                                    Detailed
                                </button>
                            </div>
                        </div>
                    ) : null}

                    {/* MAIN CONTENT */}
                    <div className="ar-content">
                        <ReportRestoreOffer
                            offer={restore.offer}
                            onAccept={handleAcceptRestore}
                            onDismiss={restore.dismissOffer}
                            show={status === 'init'}
                        />
                        <ReportRestoreNote
                            servedFrom={restore.servedFrom}
                            refreshing={restore.refreshing}
                        />

                        {/* Report filter chips */}
                        {reportFilterChips.length > 0 ? (
                            <div className="ar-filter-chips">
                                {reportFilterChips.map((chip) => (
                                    <span key={chip.key} className="ar-filter-chip">{chip.label}</span>
                                ))}
                                <button className="ar-filter-chips__clear" onClick={handleClearReportFilters}>
                                    Clear All
                                </button>
                            </div>
                        ) : null}

                        {/* Init state */}
                        {status === 'init' ? (
                            <div className="ar-init-message">
                                <i className="fe fe-bar-chart-2" style={{ fontSize: '48px', color: 'var(--color-text-muted)', marginBottom: 'var(--space-4)' }} />
                                <p>Select a time range and click <strong>Run Report</strong> to generate your wellness report.</p>
                                <button
                                    className="dp-btn dp-btn--primary dp-btn--sm"
                                    onClick={() => handleRunReport({ force: true })}
                                    disabled={status === 'loading'}
                                    style={{ marginTop: 'var(--space-3)' }}
                                >
                                    <i className="fe fe-refresh-cw" style={{ marginRight: '4px' }} />
                                    {status === 'loading' ? 'Running...' : 'Run Report'}
                                </button>
                            </div>
                        ) : null}

                        {/* Loading state */}
                        {status === 'loading' ? (
                            <div className="ar-loading">
                                <div className="ar-loading__spinner" />
                                <p>Loading report data...</p>
                            </div>
                        ) : null}

                        {/* Overlimit */}
                        {status === 'ready' && overLimit ? (
                            <div className="ar-overlimit">
                                <i className="fe fe-alert-triangle" style={{ fontSize: '32px', color: 'var(--color-warning)' }} />
                                <h3>Report Too Large</h3>
                                <p>This report contains more than 5,000 records.</p>
                                {csvUrl ? (
                                    <a href={csvUrl} className="dp-btn dp-btn--primary" target="_blank" rel="noopener noreferrer">
                                        <i className="fe fe-download" style={{ marginRight: '4px' }} />
                                        Download Full Report (CSV)
                                    </a>
                                ) : null}
                            </div>
                        ) : null}

                        {/* Report data */}
                        {status === 'ready' && !overLimit ? (
                            <div className="ar-detail-view">
                                {hasInProgress ? (
                                    <p className="wr-progress-note" role="status">
                                        {reportType === 'rounds'
                                            ? 'A rounds window in this report is still in progress, so the counts are as of now. Areas not rounded on yet show as missed.'
                                            : 'A check-in window in this report is still in progress, so the counts are as of now. Residents who haven’t checked in yet show as missed.'}
                                    </p>
                                ) : null}
                                {/* Controls row — matches AlertDetailsPage pattern */}
                                <div className="ap-controls">
                                    <div className="ap-controls__left">
                                        <div className="ap-search">
                                            <svg className="ap-icon-md ap-search__icon" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                                                <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
                                            </svg>
                                            <input
                                                type="search"
                                                placeholder="Search records..."
                                                value={search}
                                                onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                                                className="ap-search__input"
                                                aria-label="Search records"
                                            />
                                        </div>
                                    </div>
                                    <div className="ap-controls__right">
                                        {sortedData.length > 0 ? (
                                            <>
                                                <button className="rp-btn rp-btn--secondary" onClick={handleExportCsv}>
                                                    <i className="fe fe-download" style={{ marginRight: '4px' }} />
                                                    CSV
                                                </button>
                                                <button className="rp-btn rp-btn--secondary" onClick={handleExportPdf}>
                                                    <i className="fe fe-file-text" style={{ marginRight: '4px' }} />
                                                    PDF
                                                </button>
                                            </>
                                        ) : null}
                                    </div>
                                </div>

                                {/* Table */}
                                {totalFiltered === 0 ? (
                                    <div className="ar-init-message" style={{ paddingTop: 'var(--space-8)' }}>
                                        <i className="fe fe-inbox" style={{ fontSize: '40px', color: 'var(--color-text-muted)', marginBottom: 'var(--space-3)' }} />
                                        <p>No data to display for this report.</p>
                                    </div>
                                ) : (
                                    <WellnessTable
                                        data={paginatedData}
                                        allData={activeData}
                                        columnDefs={activeColumnDefs}
                                        visibleColumns={visibleColumns}
                                        sortColumn={sortColumn}
                                        sortOrder={sortOrder}
                                        onSort={handleSort}
                                        filters={columnFilters}
                                        onFilterChange={handleColumnFilterChange}
                                        onClearFilters={handleClearColumnFilters}
                                        showFilters={viewMode === 'detailed'}
                                        tzOffset={tzOffset}
                                    />
                                )}

                                {/* Pagination — always shown when data exists (matches AlertDetailsPage) */}
                                {totalFiltered > 0 ? (
                                    <Pagination
                                        page={safePage}
                                        totalPages={totalPages}
                                        total={totalFiltered}
                                        perPage={perPage}
                                        onPageChange={setPage}
                                        onPerPageChange={(n) => { setPerPage(n); setPage(1); }}
                                        entityLabel="records"
                                        selectId="wr-per-page"
                                    />
                                ) : null}
                            </div>
                        ) : null}
                    </div>

                    {/* Filter Drawer */}
                    <ReportFilterDrawer
                        sections={filterSections}
                        hideResponseTime
                        dateOnly
                        open={filterDrawerOpen}
                        onClose={() => setFilterDrawerOpen(false)}
                        onApply={handleApplyFilters}
                        onClear={handleClearReportFilters}
                        filterOptions={filterOptions}
                        currentFilters={reportFilters}
                        saving={false}
                        timeRanges={TIME_RANGES}
                        rangeId={rangeId}
                        customRange={customRange}
                        start={start}
                        end={end}
                        onTimeRangeChange={handleTimeRange}
                        onCustomToggle={handleCustomToggle}
                        onStartChange={setStart}
                        onEndChange={setEnd}
                        timeRangeDisabled={isMissedCheckin}
                    />

                    {/* Schedule Report Drawer */}
                    <ScheduleReportDrawer
                        open={scheduleDrawerOpen}
                        onClose={() => setScheduleDrawerOpen(false)}
                        onSave={handleScheduleSave}
                        saving={false}
                        reportTypeId={reportTypeIdForSubType('wellness', reportType, 3)}
                        rangeId={rangeId}
                        rangeName={customRange ? 'Custom' : rangeName}
                        reportFilters={scheduleFilters}
                        filterOptions={filterOptions}
                        campusId={campusId}
                        frequencies={frequencies}
                        tzOffset={tzOffset}
                    />
                </div>
            </div>
        </>
    );
};

export default WellnessReportsPage;
